import { useQuery } from '@tanstack/react-query'
import { MoreHorizontal, Pencil, Plus, Ruler, Star, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { Can } from '@/features/auth/guards'
import { gradingScalesQuery, useDeleteGradingScale, useSaveGradingScale } from '@/features/exams/api'
import { GradingScaleDialog } from '@/features/exams/components/GradingScaleDialog'
import { formatMarks, formatScore } from '@/features/exams/status'
import type { GradingScale } from '@/features/exams/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function GradingScalesPage() {
  const { t } = useTranslation('exams')
  useDocumentTitle('Grading scales')
  const scales = useQuery(gradingScalesQuery())
  const remove = useDeleteGradingScale()
  const save = useSaveGradingScale()
  const [editing, setEditing] = useState<GradingScale | 'new' | null>(null)
  const [deleting, setDeleting] = useState<GradingScale | null>(null)

  const makeDefault = (s: GradingScale) =>
    save.mutate(
      { id: s.id, name: s.name, isDefault: true, bands: s.bands, outOf: s.outOf },
      {
        onSuccess: () => toast.success(t('Default scale changed'), s.name),
        onError: (e) => toast.error(t('Could not change the default'), errorMessage(e)),
      },
    )

  return (
    <>
      <PageHeader
        title={t('Grading scales')}
        description={t('How percentages turn into grades and grade points. Each exam uses one scale.')}
        breadcrumbs={[{ label: t('Examinations'), to: '/admin/exams' }, { label: t('Grading scales') }]}
        homeHref="/admin"
        actions={
          <Can permission="exams.manage">
            <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
              {t('New scale')}
            </Button>
          </Can>
        }
      />
      {scales.isPending ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 2 }, (_, i) => (
            <Card key={i} className="p-5">
              <Skeleton className="h-32 w-full" />
            </Card>
          ))}
        </div>
      ) : scales.error ? (
        <Card>
          <ErrorState onRetry={() => void scales.refetch()} />
        </Card>
      ) : scales.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Ruler />}
            title={t('No grading scales')}
            description={t('Create one from a template (letter grades, GPA or percentage only).')}
            action={
              <Can permission="exams.manage">
                <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
                  {t('New scale')}
                </Button>
              </Can>
            }
          />
        </Card>
      ) : (
        <ul className="grid gap-4 lg:grid-cols-2">
          {scales.data.map((s) => (
            <li key={s.id}>
              <Card className="h-full">
                <CardHeader
                  icon={<Ruler />}
                  title={
                    <span className="flex items-center gap-2">
                      {s.name}
                      {s.isDefault ? (
                        <Badge tone="brand" size="sm" dot>
                          {t('Default')}
                        </Badge>
                      ) : null}
                      {s.outOf ? (
                        <Badge tone="info" size="sm">
                          {t('Out of {{max}}', { max: s.outOf })}
                        </Badge>
                      ) : null}
                    </span>
                  }
                  description={`${t('{{count}} grades', { count: s.bands.length })} · ${t('used by {{count}} exams', { count: s.examCount })}`}
                  actions={
                    <Can permission="exams.manage">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <IconButton
                            label={t('Actions for {{name}}', { name: s.name })}
                            icon={<MoreHorizontal />}
                            size="icon-sm"
                          />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem icon={<Pencil />} onSelect={() => setEditing(s)}>
                            {t('Edit grades')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            icon={<Star />}
                            disabled={s.isDefault}
                            onSelect={() => makeDefault(s)}
                          >
                            {t('Make default')}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            icon={<Trash2 />}
                            tone="danger"
                            disabled={s.isDefault || s.examCount > 0}
                            onSelect={() => setDeleting(s)}
                          >
                            {t('Delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Can>
                  }
                />
                <table className="w-full text-sm">
                  <caption className="sr-only">{t('Grades of {{name}}', { name: s.name })}</caption>
                  <thead>
                    <tr className="text-left text-2xs font-semibold tracking-wider text-muted uppercase">
                      <th scope="col" className="px-5 py-2">
                        {t('Grade')}
                      </th>
                      <th scope="col" className="px-5 py-2">
                        {t('Range')}
                      </th>
                      <th scope="col" className="px-5 py-2 text-right">
                        {t('Grade point')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.bands.map((b) => (
                      <tr key={b.label} className="border-t border-line">
                        <td className="px-5 py-2">
                          <Badge tone={b.isPass ? 'success' : 'danger'} size="sm">
                            {b.label}
                          </Badge>
                        </td>
                        <td className="px-5 py-2 text-ink-soft tabular">
                          {formatMarks(b.minPercent)} – {formatMarks(b.maxPercent)}%
                          {s.outOf ? (
                            <span className="text-muted">
                              {' '}
                              ({formatScore((b.minPercent * s.outOf) / 100)} –{' '}
                              {formatScore((b.maxPercent * s.outOf) / 100)} / {s.outOf})
                            </span>
                          ) : null}
                        </td>
                        <td className="px-5 py-2 text-right tabular">
                          {b.gradePoint === null ? '—' : b.gradePoint.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {editing !== null ? (
        <GradingScaleDialog
          open
          onOpenChange={(o) => (o ? undefined : setEditing(null))}
          scale={editing === 'new' ? null : editing}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(o) => (o ? undefined : setDeleting(null))}
          title={t('Delete {{name}}?', { name: deleting.name })}
          description={t('The scale and its grades are removed. This cannot be undone.')}
          confirmLabel={t('Delete scale')}
          loading={remove.isPending}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                toast.success(t('Grading scale deleted'), deleting.name)
                setDeleting(null)
              },
              onError: (e) => toast.error(t('Could not delete'), errorMessage(e)),
            })
          }
        />
      ) : null}
    </>
  )
}
